import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { AcpRecipeSearchService } from '../../../../services/recipe/acp-recipe-search.service';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { ConfigService } from '../../../../services/config.service';
import {
  AutoCompleteRecipeData,
  AcpResponseAPI,
  RecipeSearchField,
  RecipeSearchType,
} from '../../../../shared/models/recipe/recipe-acp.model';
import { FieldFilter } from '../../../../shared/models/search.model';
import { SearchQueryParams } from '../../../../shared/models/searchQueryParams.model';

@Component({
  selector: 'app-recipe-search-bar',
  templateUrl: './recipe-search-bar.component.html',
  styleUrls: ['./recipe-search-bar.component.scss'],
  imports: [
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatInputModule,
    MatAutocompleteModule,
    AsyncPipe,
    NgForOf,
    MatIconModule,
    NgIf,
    MatChipsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecipeSearchBarComponent implements OnInit, OnDestroy {
  @Input() fieldFilters: FieldFilter[] = [];
  @Output() searchBarChange = new EventEmitter<AcpResponseAPI>();
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  searchControl: FormControl<string> = new FormControl();
  selectedItems: { value: string; type: string }[] = [];
  filteredOptions: Observable<AutoCompleteRecipeData>;
  searchQueryParams: SearchQueryParams;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private acpRecipeSearchService: AcpRecipeSearchService,
    private configService: ConfigService,
    private translateService: TranslateService,
  ) {}

  /**
   * On init lifecycle hook
   * Set the language and the filtered options for the autocomplete input field
   */
  ngOnInit(): void {
    this.searchQueryParams = this.configService.getDefaultSearchQueryParams();
    this.configService.searchQueryParamsChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      location.reload();
    });

    this.filteredOptions = this.searchControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query: string) => {
        if (query.length < 2) {
          return of(null);
        }
        return this.acpRecipeSearchService.acpRecipeSearch(
          this.fieldFilters,
          this.searchQueryParams.market,
          this.searchQueryParams.lang,
          query,
        );
      }),
      map((apiData) =>
        apiData
          ? {
            ingredient: apiData.fieldFilters?.ingredient,
            recipe: apiData.fieldFilters?.recipe,
          }
          : null,
      ),
      filter((data): data is AutoCompleteRecipeData => data !== null),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * On selection changed event handler for the autocomplete input field to add the selected item to the selectedItems array
   * @param event
   */
  onSelectionChanged(event: MatAutocompleteSelectedEvent): void {
    const { data: value, field } = event.option.value;
    const type = this.translateType(field);

    const index = this.selectedItems.findIndex((item) => item.type === type);
    if (index > -1) {
      this.selectedItems[index] = { value, type };
    } else {
      this.selectedItems.push({ value, type });
    }

    this.searchInput.nativeElement.value = '';
    this.searchControl.setValue('');
    this.emitSearchBarChange();
  }

  /**
   * Remove the selected item from the selectedItems array
   * @param item
   */
  remove(item: string): void {
    const index = this.selectedItems.findIndex((selectedItem) => selectedItem.value === item);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
    }
    this.emitSearchBarChange();
  }

  /**
   * On search event handler for the search button
   */
  search(): void {
    this.searchBarChange.emit({
      query: this.searchInput.nativeElement.value,
    });
  }

  /**
   * Emit the search bar change event with the selected items and the query
   */
  emitSearchBarChange(): void {
    const fieldFilters = {
      ingredient: {
        field: RecipeSearchField.INGREDIENT,
        values: this.getSelectedValuesByType(RecipeSearchType.INGREDIENT),
      },
      recipe: {
        field: RecipeSearchField.RECIPE,
        values: this.getSelectedValuesByType(RecipeSearchType.RECIPE),
      },
    };

    const query = this.searchInput.nativeElement.value;

    this.searchBarChange.emit({ fieldFilters, query });
  }

  /**
   * Get the selected values by type
   * @param type
   * @private
   */
  private getSelectedValuesByType(type: string): string[] {
    return this.selectedItems.filter((item) => item.type === this.translateService.instant(type)).map((item) => item.value);
  }

  /**
   * Translate the field to the corresponding type
   * @param field
   * @private
   */

  private translateType(field: string): string {
    return field === RecipeSearchField.INGREDIENT
      ? this.translateService.instant(RecipeSearchType.INGREDIENT)
      : this.translateService.instant(RecipeSearchType.RECIPE);
  }
}
